import { NgForOf } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, inject, Output, signal, ViewChild, WritableSignal } from '@angular/core';
import { getIdToken } from '@angular/fire/auth';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { BaseComponent } from '../../shared/base.component';
import { ViewDealerRepsState } from '../../shared/state-interface';
import { DealerNamesComponent } from '../../ui-components/dealer-names/dealer-names.component';
import { SiteHeaderComponent } from '../../site-header/site-header.component';
import { DealerRep } from '../shared/dealer-rep.model';
import { DealerRepService } from '../shared/dealer-rep.service';

@Component({
    selector: 'app-view-dealer-reps',
    standalone: true,
    imports: [
        ReactiveFormsModule,
        MatInputModule,
        MatButtonModule,
        MatExpansionModule,
        MatTableModule,
        MatIconModule,
        MatMenuModule,
        MatPaginatorModule,
        NgForOf,
        SiteHeaderComponent,
        DealerNamesComponent
    ],
    templateUrl: './view-dealer-reps.component.html',
    styleUrl: './view-dealer-reps.component.css'
})
export class ViewDealerRepsComponent extends BaseComponent implements AfterViewInit {

    @ViewChild(DealerNamesComponent) dealerNamesComponent!:DealerNamesComponent;
    @Output() messageEvent = new EventEmitter<string>();

    private dealerRepService: DealerRepService = inject(DealerRepService);

    private static _retrievingDealerReps = "Retrieving Dealer Reps";
    private static _retrievingDealerRepsError = `${ViewDealerRepsComponent._retrievingDealerReps} error:`;
    private static _inactivatingDealerRep = "Inactivating Dealer Rep";
    private static _inactivatingDealerRepError = `${ViewDealerRepsComponent._inactivatingDealerRep} error:`;
    private static _reactivatingDealerRep = "Reactivating Dealer Rep";
    private static _reactivatingDealerRepError = `${ViewDealerRepsComponent._reactivatingDealerRep} error:`;
    private static _deletingDealerRep = "Deleting Dealer Rep";
    private static _deletingDealerRepError = `${ViewDealerRepsComponent._deletingDealerRep} error:`;

    protected viewDealerRepsForm = new FormGroup({
        lastName: new FormControl(""),
        firstName: new FormControl(""),
        email: new FormControl(""),
        dealerRepId: new FormControl(""),
    });

    private _dealerReps: WritableSignal<DealerRep[]> = signal<DealerRep[]>([]);
    private _dealerRepCount: WritableSignal<number> = signal<number>(-1);

    public ngAfterViewInit() {
        // The <app-dealer-names-component/> will not be rendered
        // in the HTML for some user types.
        //
        // The dealerNamesComponent will be undefined if
        // <app-dealer-names-component/> is not rendered in the HTML.

        const state: ViewDealerRepsState | undefined = this.stateManager.viewDealerRepsState;

        if (state != undefined) {
            this.viewDealerRepsForm = state.viewDealerRepsForm;
            if (this.dealerNamesComponent != undefined) {
                this.dealerNamesComponent.restoreState(state.dealerNamesComponentState!);
            }

            if (this.refresh) {
                this.pageIndex = 0;
                this.dealerRepCount = -1;
                this.listDealerReps(this.overrideSnackBar);
            } else {
                this.dealerReps = state.dealerReps;
                this.dealerRepCount = state.dealerRepCount;
                super.restoreState(state.baseComponentState);
            }
        } else if (this.dealerNamesComponent != undefined) {
            this.dealerNamesComponent.init();
        }
    }

    public ngOnDestroy() {
        const state: ViewDealerRepsState = {} as ViewDealerRepsState;
        state.viewDealerRepsForm = <any>this.viewDealerRepsForm;
        state.dealerReps = this.dealerReps;
        state.dealerRepCount = this.dealerRepCount;
        state.dealerNamesComponentState = this.dealerNamesComponent != undefined ?
                                            this.dealerNamesComponent.getState() : undefined;
        state.baseComponentState = super.getState();
        this.stateManager.viewDealerRepsState = state;
    }

    protected getAltCssClass(className: string): string {
        return (this.userType != "employee") ? className + "-alt" : "";
    }

    protected get dealerReps(): DealerRep[] {
        return this._dealerReps();
    }

    protected get dealerRepCount(): number {
        return this._dealerRepCount();
    }

    protected override handlePageEvent(e: PageEvent): void {
        super.handlePageEvent(e);
        this.listDealerReps();
    }

    protected async listDealerReps(): Promise<void>;
    protected async listDealerReps(overrideSnackBar: string | undefined): Promise<void>;
    protected async listDealerReps(overrideSnackBar?: string): Promise<void> {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            if (this.searchParamsDirty()) {
                this.pageIndex = 0;
                this.dealerRepCount = -1;
                this.markSearchParams(super.pristine);
            }

            const observable = await this.dealerRepService.getDealerReps(await getIdToken(currentUser),
                this.getSearchParams(), this.pageSize, this.pageIndex);

            if (overrideSnackBar == undefined) {
                super.showSnackBar(`${ViewDealerRepsComponent._retrievingDealerReps}...`, -1)
            }

            super.message = "";

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));

                    if (json["get-dealer-reps"] == "success") {
                        // this.dealerReps is a signal. The view will be refreshed
                        // upon update of the signal.
                        this.dealerReps = DealerRep.toDealerReps(json["dealer-reps"]);
                        this.dealerRepCount = json["dealer-rep-count"];
                        this.message = "";
                        overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar);
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    overrideSnackBar == undefined ? super.dismissSnackBar() : super.showSnackBar(overrideSnackBar, 30);
                    const message = `${ViewDealerRepsComponent._retrievingDealerRepsError} ${(<Error>error).message}`;
                    super.message = message;
                }
            })
        } else {
            const message = `${ViewDealerRepsComponent._retrievingDealerRepsError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected async resetSearch() {
        this.lastNameCtl.setValue("");
        this.firstNameCtl.setValue("");
        this.emailCtl.setValue("");

        if (this.dealerNamesComponent != undefined) {
            this.dealerNamesComponent.dealerCtl.setValue("");
        }

        this.dealerRepIdCtl.setValue("");
        this.markSearchParams(super.dirty);
        this.listDealerReps();
    }

    protected async addDealerRep() {
        this.stateManager.addDealerRepState = undefined;
        this.router.navigate(["/add-dealer-rep"]);
    }

    protected async updateDealerRep(id: number) {
        this.stateManager.updateDealerRepState = undefined;
        this.router.navigate([`/update-dealer-rep/${id}`]);
    }
    
    protected async inactivateDealerRep(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.dealerRepService.inactivateDealerRep(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewDealerRepsComponent._inactivatingDealerRep}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["inactivate-dealer-rep"] == "success") {
                        this.listDealerReps("Dealer Rep inactivated.");
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewDealerRepsComponent._inactivatingDealerRepError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewDealerRepsComponent._inactivatingDealerRepError} Null user instance`;
            console.error(message);
            super.showSnackBar(message, 30);
        }
    }

    protected async reactivateDealerRep(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.dealerRepService.reactivateDealerRep(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewDealerRepsComponent._reactivatingDealerRep}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["reactivate-dealer-rep"] == "success") {
                        this.listDealerReps("Dealer Rep reactivated.");
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewDealerRepsComponent._reactivatingDealerRepError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewDealerRepsComponent._reactivatingDealerRepError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    public async deleteDealerRepLogical(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.dealerRepService.deleteDealerRepLogical(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewDealerRepsComponent._deletingDealerRep}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["logical-delete-dealer-rep"] == "success") {
                        if (this.dealerReps.length == 1) {
                            // Nothing left on current page, backup
                            // one page.
                            this.pageIndex -= 1;
                        }

                        this.listDealerReps("Dealer Rep deleted.");
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewDealerRepsComponent._deletingDealerRepError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewDealerRepsComponent._deletingDealerRepError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    public async deleteDealerRep(id: number) {
        const currentUser = this.auth.currentUser;

        if (currentUser) {
            const observable = await this.dealerRepService.deleteDealerRep(await getIdToken(currentUser), id);
            super.showSnackBar(`${ViewDealerRepsComponent._deletingDealerRep}...`, -1);

            observable.subscribe(response => {
                try {
                    const json = JSON.parse(JSON.stringify(response));
                    if (json["delete-dealer-rep"] == "success") {
                        if (this.dealerReps.length == 1) {
                            // Nothing left on current page, backup
                            // one page.
                            this.pageIndex -= 1;
                        }

                        this.listDealerReps("Dealer Rep deleted.");
                        this.message = "";
                    } else {
                        throw new Error(json["message"]);
                    }
                } catch (error) {
                    const message = `${ViewDealerRepsComponent._deletingDealerRepError} ${(<Error>error).message}`;
                    console.error(message);
                    super.showSnackBar(message, 30);
                }
            })
        } else {
            const message = `${ViewDealerRepsComponent._deletingDealerRepError} Null user instance`;
            console.error(message)
            super.showSnackBar(message, 30);
        }
    }

    protected searchParamsDirty(): boolean {
        return this.lastNameCtl.dirty
            || this.firstNameCtl.dirty
            || this.emailCtl.dirty
            || (this.dealerNamesComponent != undefined ? this.dealerNamesComponent.dealerCtl.dirty: false)
            || this.dealerRepIdCtl.dirty;
    }

    protected markSearchParams(func: Function) {
        super.markControl(this.lastNameCtl, func);
        super.markControl(this.firstNameCtl, func);
        super.markControl(this.emailCtl, func);

        if (this.dealerNamesComponent != undefined) {
            super.markControl(this.dealerNamesComponent.dealerCtl, func);
        }
        
        super.markControl(this.dealerRepIdCtl, func);
    }

    private getSearchParams(): any {
        return {
            "lastName": this.lastNameCtl.value,
            "firstName": this.firstNameCtl.value,
            "email": this.emailCtl.value,
            "dealerId": (this.dealerNamesComponent != undefined
                        && this.dealerNamesComponent.selectedDealer > 0 ?
                            this.dealerNamesComponent.selectedDealer : undefined),
            "dealerRepId": this.dealerRepIdCtl.value,
        }
    }

    private get lastNameCtl(): FormControl {
        return this.viewDealerRepsForm.controls.lastName;
    }

    private get firstNameCtl(): FormControl {
        return this.viewDealerRepsForm.controls.firstName;
    }

    private get emailCtl(): FormControl {
        return this.viewDealerRepsForm.controls.email;
    }

    private get dealerRepIdCtl(): FormControl {
        return this.viewDealerRepsForm.controls.dealerRepId;
    }

    private set dealerReps(value: DealerRep[]) {
        this._dealerReps.set(value);
    }

    private set dealerRepCount(value: number) {
        this._dealerRepCount.set(value);
    }
}
